/**
 * 預設頁面最低權限等級（role_level，數字越小權限越高）
 * DB pagePermissions 優先；未設定時使用此預設值作為 fallback
 *
 * ROLE_LEVELS: dev=1, admin=2, sunward=3, usermanager=4, user=5, viewer=6
 */
export const DEFAULT_PAGE_MIN_LEVEL: Record<string, number> = {
  '/': 6,              // 系統總覽
  '/monitoring': 6,    // 設備監控
  '/schedule': 6,      // 冷藏庫排程
  '/vibration': 6,     // AI振動分析
  '/energy': 6,        // 能源管理（EMS）— 所有人可見
  '/alarm': 6,         // 警報管理
  '/history': 6,       // 歷史查詢
  '/report': 6,        // 報表管理 — 所有人（包含原始數據選項僅 dev/admin）
  '/audit': 2,         // 稽核日誌 — admin 以上
  '/floor-plan': 6,    // 全局總覽 — 所有人可見
  '/system': 6,        // 系統管理 — 所有人可進入（敏感 tab 另有 requiredLevel 控管）
  '/info': 6,          // 資訊（關於／操作說明／下載）— 所有登入者
  // 系統資訊子分頁：馬修目前僅 dev(1)/admin(2)/東海(3) 三種角色，設 4 等同全部可見；
  // 仍照 T 棟設 4，未來若新增低權限角色即自動受保護。後端 API 需用同一等級擋，
  // 不可只在前端隱藏（階段 2 實作 /api/system/info 時一併處理）。
  '/info:sysinfo': 4,  // 系統資訊（版本／磁碟／網路／服務）
};

/**
 * 判斷使用者角色是否有任何警報類型啟用了通知
 * alarmRoleFilter 為空 = 所有角色皆接收 → return true
 *
 * ⚠️ 2026-10-23：`=== true` → `!== false`。
 *    這支決定「要不要讓該角色進得去警報頁」，用舊規則時，
 *    一個從未被明確勾選過的角色會被判定成「完全沒有任何通知權限」而整頁進不去。
 *    五處同步，詳見 stores/alarmStore.ts 的註解。
 */
export function hasAlarmNotifyAccess(
  role: string,
  alarmRoleFilter: Record<string, Record<string, boolean>>,
): boolean {
  if (!alarmRoleFilter || Object.keys(alarmRoleFilter).length === 0) return true;
  return Object.values(alarmRoleFilter).some((roleMap) => roleMap?.[role] !== false);
}

/**
 * 判斷使用者是否有權限進入指定頁面
 * 優先使用 DB pagePermissions，未設定時 fallback 至 DEFAULT_PAGE_MIN_LEVEL
 * 頁面權限與通知權限分離：通知決定鈴鐺數字，頁面權限決定能否進入
 */
export function hasPageAccess(
  path: string,
  roleLevel: number,
  role: string,
  pagePermissions: Record<string, Record<string, boolean>>,
  _alarmRoleFilter?: Record<string, Record<string, boolean>>,
): boolean {
  // dev（role_level = 1）一律放行；admin 由權限矩陣控制
  if (roleLevel <= 1) return true;

  // DB 有此頁面的設定 → 明確設為 true/false 的角色直接採用；未列出的角色 fallback 預設等級
  const perms = pagePermissions[path];
  if (perms && Object.keys(perms).length > 0 && perms[role] !== undefined) {
    return perms[role] === true;
  }

  // DB 無此角色的設定 → 依預設最低權限等級
  const minLevel = DEFAULT_PAGE_MIN_LEVEL[path];
  if (minLevel === undefined) return false; // 未定義的路徑預設拒絕
  return roleLevel <= minLevel;
}

/**
 * 預設 Tab 最低權限等級（從 tabRegistry requiredLevel 衍生）
 * 未列出的 tab 預設 level 6（所有人可見）
 */
export const DEFAULT_TAB_MIN_LEVEL: Record<string, number> = {
  '/system:users': 4,       // 使用者管理 — usermanager 以上
  '/system:settings': 2,    // 系統設定 — admin 以上
  '/alarm:custom_rules': 2, // 自訂警報規則 — admin 以上
  '/alarm:escalation': 2,   // 警報升級政策 — admin 以上
  '/report:schedules': 2,   // 排程管理 — admin 以上
};

/**
 * 判斷使用者是否有權限查看指定 Tab
 * 前提：使用者已通過該頁面的 hasPageAccess 檢查
 *
 * 優先使用 DB pagePermissions 中的 compound key（如 "/monitoring:cold"），
 * 未設定時 fallback 至 DEFAULT_TAB_MIN_LEVEL
 */
export function hasTabAccess(
  pagePath: string,
  tabKey: string,
  roleLevel: number,
  role: string,
  pagePermissions: Record<string, Record<string, boolean>>,
): boolean {
  // dev 一律放行；admin 由權限矩陣控制
  if (roleLevel <= 1) return true;

  const compoundKey = `${pagePath}:${tabKey}`;

  // DB 有此 tab 的設定 → 明確設為 true/false 的角色直接採用；未列出的角色 fallback 預設等級
  const perms = pagePermissions[compoundKey];
  if (perms && Object.keys(perms).length > 0 && perms[role] !== undefined) {
    return perms[role] === true;
  }

  // DB 無此角色的設定 → 依預設最低權限等級
  const minLevel = DEFAULT_TAB_MIN_LEVEL[compoundKey];
  if (minLevel === undefined) return true; // 無限制的 tab 預設放行
  return roleLevel <= minLevel;
}
